/**
 * Degradados por skill: texto animado vía CSS; iconos vía fill url(#skill-grad-{key}) en SkillGradientDefs.
 */

export type GradientPreset = {
  stops: string[];
  deg: number;
  /** Duración del desplazamiento del degradado en el texto (background-position) */
  textShiftSec: number;
  /** Color representativo para sombras hover (hex) */
  glowHex: string;
  /** Si el degradado SVG debe rotar (multi-color / dinámico) */
  rotateSvg?: boolean;
  /** Duración rotación SVG (s) */
  svgRotateSec?: number;
};

function includesAny(haystack: string, needles: string[]): boolean {
  return needles.some((n) => haystack.includes(n));
}

/** Presets: claves usadas en SkillGradientDefs (id = skill-grad-{key}) */
export const SKILL_GRADIENT_PRESETS: Record<string, GradientPreset> = {
  default: {
    stops: ['#475569', '#94a3b8', '#64748b', '#475569'],
    deg: 125,
    textShiftSec: 10,
    glowHex: '#64748b',
    rotateSvg: true,
    svgRotateSec: 48,
  },
  antigravity: {
    stops: ['#4285F4', '#4285F4', '#4285F4', '#5A95F7', '#34A853', '#FBBC05', '#EA4335'],
    deg: 135,
    textShiftSec: 9,
    glowHex: '#5A95F7',
    rotateSvg: true,
    svgRotateSec: 14,
  },
  openclaw: {
    stops: ['#4f0f0f', '#701c1c', '#fa1010', '#4f0f0f', '#701c1c', '#fa1010'],
    deg: 90,
    textShiftSec: 20,
    glowHex: '#b91c1c',
    rotateSvg: true,
    svgRotateSec: 20,
  },
  n8n: {
    stops: ['#7c2d12', '#9a3412', '#c2410c', '#ea580c', '#fb923c'],
    deg: 90,
    textShiftSec: 7,
    glowHex: '#FF6D5A',
    rotateSvg: true,
    svgRotateSec: 22,
  },
  ollama: {
    stops: ['#059669', '#34d399', '#10b981', '#059669'],
    deg: 120,
    textShiftSec: 9,
    glowHex: '#10B981',
    rotateSvg: true,
    svgRotateSec: 26,
  },
  claudecode: {
    stops: ['#F57C3D', '#D97706', '#F59E0B', '#F57C3D'],
    deg: 125,
    textShiftSec: 8,
    glowHex: '#F57C3D',
    rotateSvg: true,
    svgRotateSec: 20,
  },
  cursor: {
    stops: ['#afafaf', '#a2a2a2', '#ffffff', '#afafaf', '#ffffff'],
    deg: 145,
    textShiftSec: 11,
    glowHex: '#fafafa',
    rotateSvg: true,
    svgRotateSec: 30,
  },
  documentation: {
    stops: ['#64748b', '#94a3b8', '#cbd5e1', '#64748b'],
    deg: 118,
    textShiftSec: 12,
    glowHex: '#64748b',
    rotateSvg: true,
    svgRotateSec: 40,
  },
  mcpa2a: {
    stops: ['#312e81', '#4f46e5', '#6366f1', '#22d3ee', '#818cf8', '#4f46e5'],
    deg: 135,
    textShiftSec: 8,
    glowHex: '#6366F1',
    rotateSvg: true,
    svgRotateSec: 18,
  },
  cursorskills: {
    stops: ['#5b21b6', '#7c3aed', '#a78bfa', '#c4b5fd', '#8b5cf6', '#7c3aed'],
    deg: 125,
    textShiftSec: 9,
    glowHex: '#8B5CF6',
    rotateSvg: true,
    svgRotateSec: 20,
  },
  scrumai: {
    stops: ['#0052CC', '#2684FF', '#36B37E', '#6554C0', '#00B8D9', '#0052CC'],
    deg: 130,
    textShiftSec: 8,
    glowHex: '#36B37E',
    rotateSvg: true,
    svgRotateSec: 19,
  },
  react: {
    stops: ['#61DAFB', '#39c5e8', '#1aa9d8', '#0f8fbe'],
    deg: 135,
    textShiftSec: 7,
    glowHex: '#61DAFB',
    rotateSvg: true,
    svgRotateSec: 18,
  },
  javascript: {
    stops: ['#e8b923', '#f7df1e', '#d4a017', '#e8b923'],
    deg: 95,
    textShiftSec: 8,
    glowHex: '#D4A017',
    rotateSvg: true,
    svgRotateSec: 20,
  },
  typescript: {
    stops: ['#235a97', '#3178c6', '#5a9fd4', '#3178c6'],
    deg: 130,
    textShiftSec: 8,
    glowHex: '#3178C6',
    rotateSvg: true,
    svgRotateSec: 20,
  },
  next: {
    stops: ['#afafaf', '#404040', '#737373', '#404040'],
    deg: 145,
    textShiftSec: 10,
    glowHex: '#333333',
    rotateSvg: true,
    svgRotateSec: 24,
  },
  angular: {
    stops: ['#a5002f', '#dd0031', '#ff4569', '#dd0031'],
    deg: 128,
    textShiftSec: 7,
    glowHex: '#DD0031',
    rotateSvg: true,
    svgRotateSec: 18,
  },
  ionic: {
    stops: ['#1e5ecc', '#3880ff', '#5ca8ff', '#3880ff'],
    deg: 120,
    textShiftSec: 8,
    glowHex: '#3880FF',
    rotateSvg: true,
    svgRotateSec: 22,
  },
  flutter: {
    stops: ['#02569B', '#54c5f8', '#0175c2', '#02569B'],
    deg: 135,
    textShiftSec: 8,
    glowHex: '#02569B',
    rotateSvg: true,
    svgRotateSec: 20,
  },
  css: {
    stops: ['#1d4f91', '#3b82f6', '#93c5fd','#3d1d96', '#1d4f91'],
    deg: 125,
    textShiftSec: 9,
    glowHex: '#3B82F6',
    rotateSvg: true,
    svgRotateSec: 20,
  },
  reactNative: {
    stops: ['#0ea5b7', '#0f766e', '#155e75', '#0ea5b7'],
    deg: 135,
    textShiftSec: 8,
    glowHex: '#0F766E',
    rotateSvg: true,
    svgRotateSec: 18,
  },
  html: {
    stops: ['#e34f26', '#f06529', '#eb8840', '#e34f26'],
    deg: 125,
    textShiftSec: 7,
    glowHex: '#E34F26',
    rotateSvg: true,
    svgRotateSec: 17,
  },
  java: {
    stops: ['#4d7c2a', '#6db33f', '#8fd46a', '#6db33f'],
    deg: 115,
    textShiftSec: 9,
    glowHex: '#6DB33F',
    rotateSvg: true,
    svgRotateSec: 24,
  },
  dotnet: {
    stops: ['#5d4da6', '#512bd4', '#7c3aed', '#5d4da6'],
    deg: 135,
    textShiftSec: 8,
    glowHex: '#512BD4',
    rotateSvg: true,
    svgRotateSec: 20,
  },
  python: {
    stops: ['#3776AB', '#3776AB', '#FFD43B', '#ffc107'],
    deg: 90,
    textShiftSec: 8,
    glowHex: '#3776AB',
    rotateSvg: false,
    svgRotateSec: 0,
  },
  api: {
    stops: ['#6366f1', '#8b5cf6', '#a78bfa', '#6366f1'],
    deg: 125,
    textShiftSec: 9,
    glowHex: '#6366F1',
    rotateSvg: true,
    svgRotateSec: 22,
  },
  node: {
    stops: ['#215732', '#339933', '#5cb85c', '#339933'],
    deg: 118,
    textShiftSec: 8,
    glowHex: '#339933',
    rotateSvg: true,
    svgRotateSec: 21,
  },
  mongo: {
    stops: ['#13aa52', '#47a248', '#7dd87f', '#47a248'],
    deg: 120,
    textShiftSec: 8,
    glowHex: '#47A248',
    rotateSvg: true,
    svgRotateSec: 23,
  },
  git: {
    stops: ['#f05032', '#f79b82', '#f05032'],
    deg: 90,
    textShiftSec: 7,
    glowHex: '#F05032',
    rotateSvg: true,
    svgRotateSec: 19,
  },
  illustrator: {
    stops: ['#ff8c00', '#ff9a00', '#ffc14d', '#ff9a00'],
    deg: 105,
    textShiftSec: 8,
    glowHex: '#FF9A00',
    rotateSvg: true,
    svgRotateSec: 22,
  },
  photoshop: {
    stops: ['#001d26', '#31a8ff', '#87cefa', '#31a8ff'],
    deg: 135,
    textShiftSec: 9,
    glowHex: '#31A8FF',
    rotateSvg: true,
    svgRotateSec: 24,
  },
  xd: {
    stops: ['#ff61f6', '#ff26d9', '#ffa0f0', '#ff61f6'],
    deg: 120,
    textShiftSec: 8,
    glowHex: '#FF61F6',
    rotateSvg: true,
    svgRotateSec: 18,
  },
  figma: {
    stops: ['#ff1515', '#f24e1e', '#a259ff', '#2496ed', '#54b4f3', '#0acf83', '#ff1515'],
    deg: 130,
    textShiftSec: 10,
    glowHex: '#A259FF',
    rotateSvg: true,
    svgRotateSec: 15,
  },
  uiux: {
    stops: ['#6366f1', '#ec4899', '#8b5cf6', '#6366f1'],
    deg: 125,
    textShiftSec: 9,
    glowHex: '#6366F1',
    rotateSvg: true,
    svgRotateSec: 21,
  },
  docker: {
    stops: ['#0b6fa8', '#2496ed', '#54b4f3', '#2496ed'],
    deg: 128,
    textShiftSec: 8,
    glowHex: '#2496ED',
    rotateSvg: true,
    svgRotateSec: 22,
  },
  jenkins: {
    stops: ['#a51e28', '#d24939', '#e85d4c', '#d24939'],
    deg: 115,
    textShiftSec: 8,
    glowHex: '#D24939',
    rotateSvg: true,
    svgRotateSec: 23,
  },
  k8s: {
    stops: ['#1a4fd6', '#326ce5', '#7c9ef0', '#326ce5'],
    deg: 125,
    textShiftSec: 8,
    glowHex: '#326CE5',
    rotateSvg: true,
    svgRotateSec: 20,
  },
  cicd: {
    stops: ['#2088ff', '#6366f1', '#38bdf8', '#2088ff'],
    deg: 120,
    textShiftSec: 9,
    glowHex: '#2088FF',
    rotateSvg: true,
    svgRotateSec: 22,
  },
};

export function resolveSkillGradientKey(skillName: string): string {
  const s = skillName.toLowerCase().trim();

  if (includesAny(s, ['antigravity'])) return 'antigravity';
  if (includesAny(s, ['openclaw', 'clawbot', 'cloudbot'])) return 'openclaw';
  if (includesAny(s, ['n8n'])) return 'n8n';
  if (includesAny(s, ['ollama'])) return 'ollama';
  if (includesAny(s, ['claude code', 'claude ai', 'claude'])) return 'claudecode';
  if (includesAny(s, ['cursor'])) return 'cursor';
  if (
    includesAny(s, [
      'documentation',
      'documentación',
      'dokumentation',
      'documentação',
      '文档',
      'ドキュメンテーション',
      'документация',
      'التوثيق',
      'प्रलेखन',
    ])
  ) {
    return 'documentation';
  }

  if (includesAny(s, ['mcp / a2a', 'mcp/a2a', 'mcp', 'a2a'])) return 'mcpa2a';
  if (s === 'skills') return 'cursorskills';
  if (includesAny(s, ['scrum+ai', 'scrum'])) return 'scrumai';

  if (includesAny(s, ['react native'])) return 'reactNative';
  if (includesAny(s, ['react'])) return 'react';

  if (includesAny(s, ['typescript'])) return 'typescript';

  if (includesAny(s, ['javascript']) && !includesAny(s, ['typescript'])) return 'javascript';

  if (includesAny(s, ['next'])) return 'next';

  if (includesAny(s, ['angular'])) return 'angular';

  if (includesAny(s, ['ionic'])) return 'ionic';

  if (includesAny(s, ['flutter'])) return 'flutter';

  if (includesAny(s, ['css', 'sass', 'scss'])) return 'css';

  if (includesAny(s, ['html'])) return 'html';

  if (includesAny(s, ['spring', 'java'])) return 'java';

  if (includesAny(s, ['c#', '.net', 'dotnet'])) return 'dotnet';

  if (includesAny(s, ['python'])) return 'python';

  if (includesAny(s, ['api integration', 'api'])) return 'api';

  if (includesAny(s, ['node'])) return 'node';

  if (includesAny(s, ['mongo'])) return 'mongo';

  if (includesAny(s, ['git', 'gitlab'])) return 'git';

  if (includesAny(s, ['illustrator'])) return 'illustrator';

  if (includesAny(s, ['photoshop'])) return 'photoshop';

  if (includesAny(s, ['adobe xd', ' xd'])) return 'xd';

  if (includesAny(s, ['figma'])) return 'figma';

  if (includesAny(s, ['ui/ux', 'ux design', 'ui design'])) return 'uiux';

  if (includesAny(s, ['docker'])) return 'docker';

  if (includesAny(s, ['jenkins'])) return 'jenkins';

  if (includesAny(s, ['kubernetes', 'k8s', 'k8'])) return 'k8s';

  if (includesAny(s, ['ci/cd', 'cicd', 'ci cd'])) return 'cicd';

  return 'default';
}

export function getGradientPreset(skillName: string): GradientPreset {
  const key = resolveSkillGradientKey(skillName);
  return SKILL_GRADIENT_PRESETS[key] ?? SKILL_GRADIENT_PRESETS.default;
}

/** Degradado CSS para texto (animación vía background-position en styled) */
export function getCssLinearGradientForText(skillName: string): string {
  const p = getGradientPreset(skillName);
  const n = p.stops.length;
  const stops = p.stops.map((c, i) => `${c} ${(i / Math.max(1, n - 1)) * 100}%`).join(', ');
  return `linear-gradient(${p.deg}deg, ${stops})`;
}

/** Color para sombras hover (--skill-brand) */
export function getGlowColorHex(skillName: string): string {
  return getGradientPreset(skillName).glowHex;
}
